<script setup lang="ts">
  import { useModal } from '@/components/modal'

  defineProps<{
    title?: string,
    description?: string,
    size?: 'small' | 'medium' | 'large',
  }>()

  const { modal, isOpen, open, close, dialogPanel } = useModal()

  defineExpose({ open, close })
</script>

<template>
  <teleport to="body">
    <transition
      name="fade"
      mode="out-in">
      <div
        v-if="isOpen"
        class="inset-0 fixed z-40 bg-black/50"></div>
    </transition>
  </teleport>

  <dialog ref="modal">
    <!-- dark overlay -->

    <div
      class="fixed top-1/2 w-full -translate-y-1/2 z-50 left-1/2 -translate-x-1/2"
      :class="{
        'max-w-4xl': size === 'large',
        'max-w-xl': size === 'medium',
        'max-w-100': !['large', 'medium'].includes(size ?? 'small'),
      }">
      <transition
        name="slide-down"
        mode="out-in">
        <div
          v-if="isOpen"
          ref="dialogPanel"
          class="w-full dialog-panel">
          <app-button
            class="absolute flex-center rounded-full text-gray-500 hover:bg-gray-200 right-4 top-5 h-8 w-8"
            @click="close">
            <fa-icon icon="i-fas-xmark" />
          </app-button>

          <app-modal-title v-if="title">
            {{ title }}
          </app-modal-title>

          <div class="dialog-content">
            <slot></slot>
          </div>
        </div>
      </transition>
    </div>
  </dialog>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  dialog {
    @apply bg-transparent w-9/10 overflow-visible focus:outline-0;
  }
  dialog::backdrop {
    background: transparent;
  }
  dialog,
  .dialog-panel {
    max-height: calc(100vh - 2rem);
  }

  .dialog-panel {
    @apply relative rounded-2xl bg-white p-6 text-left shadow-xl w-full;
    /* make the padding-bottom appear larger when has footer */
    &:has(.dialog-footer) {
      @apply pb-20;
    }
  }
  .dialog-content {
    @apply pl-1 pr-2.5 scrollbar-stable -ml-1 -mr-4;

    /* ensure the modal content height will not surpass screen height */
    @apply overflow-y-auto;
    @apply max-h-[calc(100vh-10rem)] 2xl:max-h-[calc(100vh-16rem)];
  }
</style>
